import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LOGO_URL = "/logo.jpg";

// New logo is already tightly composed — no crop needed
const LogoMark = ({ size = "h-14 w-14" }) => (
  <div className={`${size} grid place-items-center shrink-0`}>
    <img
      src={LOGO_URL}
      alt="MR Crusher logo"
      className="h-full w-full object-contain"
    />
  </div>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <header
      className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/85 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-16 h-20 md:h-24 flex items-center justify-between relative">
        {/* Left: logo (and brand text on desktop) */}
        <Link
          to="/info"
          data-testid="brand-logo"
          className="flex items-center gap-3 z-10"
        >
          <LogoMark size="h-14 w-14 md:h-16 md:w-16" />
          <span className="font-display font-bold text-xl md:text-2xl tracking-tight uppercase text-slate-900 hidden md:inline">
            MR Crusher
          </span>
        </Link>

        {/* Center: brand name on mobile only */}
        <Link
          to="/info"
          aria-label="MR Crusher home"
          className="md:hidden absolute left-1/2 -translate-x-1/2 font-display font-bold text-lg tracking-tight uppercase text-slate-900"
          data-testid="brand-text-mobile"
        >
          MR Crusher
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8 text-sm">
          <a href="/info#how" data-testid="nav-how" className="text-slate-600 hover:text-[#0055FF] transition-colors">
            How it works
          </a>
          <a href="/info#why" data-testid="nav-why" className="text-slate-600 hover:text-[#0055FF] transition-colors">
            Why us
          </a>
          <a href="/info#contact" data-testid="nav-contact" className="text-slate-600 hover:text-[#0055FF] transition-colors">
            Contact
          </a>
          <Link to="/admin" data-testid="nav-admin" className="text-slate-900 hover:text-[#0055FF] transition-colors font-medium">
            Admin
          </Link>
        </nav>

        <Link
          to="/"
          data-testid="header-cta"
          className="hidden md:inline-flex bg-[#0055FF] hover:bg-[#003ECE] text-white px-5 py-2.5 text-sm font-medium rounded-full transition-colors z-10"
        >
          Get Instant Offer
        </Link>

        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="mobile-menu-toggle"
          className="md:hidden h-10 w-10 grid place-items-center border border-slate-300 hover:bg-slate-50 transition-colors z-10"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile dropdown panel */}
      {open && (
        <div
          className="md:hidden border-t border-slate-200 bg-white"
          data-testid="mobile-menu-panel"
        >
          <nav className="flex flex-col px-6 py-4 gap-1 text-base">
            <a
              href="/info#how"
              onClick={close}
              data-testid="mobile-nav-how"
              className="px-2 py-3 border-b border-slate-100 text-slate-700 hover:text-[#0055FF] transition-colors"
            >
              How it works
            </a>
            <a
              href="/info#why"
              onClick={close}
              data-testid="mobile-nav-why"
              className="px-2 py-3 border-b border-slate-100 text-slate-700 hover:text-[#0055FF] transition-colors"
            >
              Why us
            </a>
            <a
              href="/info#contact"
              onClick={close}
              data-testid="mobile-nav-contact"
              className="px-2 py-3 border-b border-slate-100 text-slate-700 hover:text-[#0055FF] transition-colors"
            >
              Contact
            </a>
            <Link
              to="/admin"
              onClick={close}
              data-testid="mobile-nav-admin"
              className="px-2 py-3 border-b border-slate-100 text-slate-900 font-medium hover:text-[#0055FF] transition-colors"
            >
              Admin
            </Link>
            <Link
              to="/"
              onClick={close}
              data-testid="mobile-header-cta"
              className="mt-3 inline-flex items-center justify-center bg-[#0055FF] hover:bg-[#003ECE] text-white px-6 py-3 font-medium rounded-full transition-colors"
            >
              Get Instant Offer
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
